// Use OR as prefix for all routes for Observational Research
// For any dynamic values in the route, use ##VALUE## as a placeholder which will be replaced by the actual value
// Note: Do not use these constants for API calls

import {
  CohortsFeaturePage,
  DataReliabilityPageTab,
  EvidencePlatformSidebarAppID,
  ProjectsSidebarAppFeatureId,
} from '@verantos-dev/core-enums';

import { PAGE_TABS_URL_PARAM } from './routingConstants';

// PROJECT ROUTE CONSTANTS
export const PROJECTS_BASE_PATH = EvidencePlatformSidebarAppID.projects;
export const COHORTS_PATH = ProjectsSidebarAppFeatureId.cohorts;
export const CONCEPT_GROUPS_BASE_PATH = EvidencePlatformSidebarAppID.conceptGroups;
export const ANALYTICAL_VARIABLES_BASE_PATH = EvidencePlatformSidebarAppID.analyticalVariables;
// TODO: cleanup with data_reliability ff — top-level (project-independent) Data Reliability app
export const DATA_RELIABILITY_BASE_PATH = EvidencePlatformSidebarAppID.dataReliability;
export const COHORT_VERSION_PATH = 'cohort-version';
export const COHORT_BUILDER_PATH = CohortsFeaturePage.cohortBuilder;
export const ANALYTICAL_VARIABLE_EDIT_PATH = 'analytical-variable';
export const DATA_RELIABILITY_PATH = ProjectsSidebarAppFeatureId.dataReliability;
export const ACCURACY_PATH = DataReliabilityPageTab.accuracy;
export const COMPLETENESS_PATH = DataReliabilityPageTab.completeness;
export const TRACEABILITY_PATH = DataReliabilityPageTab.traceability;

export const SUBJECT_URL_PARAM = 'subject';

// PROJECT ROUTE GETTERS
export const getProjectsBasePath = () => {
  return `/${PROJECTS_BASE_PATH}`;
};

export const getConceptGroupsBasePath = () => {
  return `/${CONCEPT_GROUPS_BASE_PATH}`;
};

export const getAnalyticalVariablesBasePath = () => {
  return `/${ANALYTICAL_VARIABLES_BASE_PATH}`;
};

// TODO: cleanup with data_reliability ff
export const getDataReliabilityBasePath = () => {
  return `/${DATA_RELIABILITY_BASE_PATH}`;
};

// Project home (agent-first project page) — see DESIGN_OR_5963_AGENT_FIRST_PROJECT_INTEGRATION.md
export const getProjectPath = (activeProjectId: string) => {
  return `${getProjectsBasePath()}/${activeProjectId}`;
};

// Agent-first nested artifact paths (phase 4). The cohort routes keep
// `cohort-version/:cohortVersionId` in the URL to remain compatible with the
// existing cohort-builder `useParams` callsites; the breadcrumb hides that
// segment from the user. See DESIGN_OR_5963_AGENT_FIRST_PROJECT_INTEGRATION.md.
export const getProjectCohortPath = (activeProjectId: string, cohortId: string) => {
  return `${getProjectPath(activeProjectId)}/cohort/${cohortId}`;
};
export const getProjectCohortVersionPath = (
  activeProjectId: string,
  cohortId: string,
  cohortVersionId: string
) => {
  return `${getProjectCohortPath(
    activeProjectId,
    cohortId
  )}/${COHORT_VERSION_PATH}/${cohortVersionId}`;
};
export const getProjectCohortVariablePath = (
  activeProjectId: string,
  cohortId: string,
  cohortVersionId: string,
  analyticalVariableId: string
) => {
  return `${getProjectCohortVersionPath(
    activeProjectId,
    cohortId,
    cohortVersionId
  )}/variable/${analyticalVariableId}`;
};
export const getProjectVariablePath = (activeProjectId: string, analyticalVariableId: string) => {
  return `${getProjectPath(activeProjectId)}/variable/${analyticalVariableId}`;
};
/**
 * Variable view nested under a cohort — preserves the "from inside
 * cohort X" context in the URL itself rather than relying on a state
 * sidecar. The breadcrumb hook parses ``cohortId`` from the path so
 * arriving at this URL renders ``Projects / <project> / <cohort> /
 * <variable>`` automatically, regardless of how the user got here
 * (criterion-modal click, deep link, browser back/forward).
 *
 * Distinct from {@link getProjectCohortVariablePath} which targets the
 * legacy cohort-version-scoped edit page (``cohort-version/:cvId/variable/:vId``).
 * This view path intentionally omits the cohort-version segment — the
 * version is a routing concern of the editor, not of read-only viewers.
 */
export const getProjectCohortVariableViewPath = (
  activeProjectId: string,
  cohortId: string,
  analyticalVariableId: string
) => {
  return `${getProjectCohortPath(activeProjectId, cohortId)}/variable/${analyticalVariableId}`;
};
// Agent-driven draft surface (OR-5963 step 6). When the agent calls
// ``draft_analytical_variable`` (or ``set_focus_scope(kind='variable_draft', ...)``), the BE
// broadcasts a ``focus_scope`` event whose ``client_id`` becomes the leaf
// segment here. The page mounts a tabbed view backed by the chat's
// in-memory ``EpiArtifact`` (looked up by slug), so the right pane follows
// the agent in real time without waiting for the AV to persist.
//
// Cohort and concept-group draft analogues intentionally absent — today
// the orchestrator only emits ``variable_draft`` automatically (the cohort
// drafter / concept-group proposer wrappers don't have draft URLs yet).
// Add them here alongside the navigator dispatch when those wrappers
// land. See ``useFocusScopeNavigator``.
// TODO: cleanup with research_assistant_gen_3 ff
export const getProjectVariableDraftPath = (activeProjectId: string, clientId: string) => {
  return `${getProjectPath(activeProjectId)}/draft/variable/${clientId}`;
};
export const getProjectDatasetPath = (activeProjectId: string, datasetId: string) => {
  return `${getProjectPath(activeProjectId)}/dataset/${datasetId}`;
};

// Analytical-dataset artifact paths (mirror cohort: profile id + version id;
// the unversioned form hits the version resolver). Defined here rather than in
// feature-analytical-datasets so lower libs can build them without importing
// that feature lib — which would close a dependency cycle.
export const ANALYTICAL_DATASETS_PATH = 'analytical-datasets';
export const ANALYTICAL_DATASET_VERSION_PATH = 'analytical-dataset-version';
export const getAnalyticalDatasetsPath = (activeProjectId: string) => {
  return `${getProjectPath(activeProjectId)}/${ANALYTICAL_DATASETS_PATH}`;
};
export const getAnalyticalDatasetDetailPath = (
  activeProjectId: string,
  analyticalDatasetProfileId: string
) => {
  return `${getAnalyticalDatasetsPath(activeProjectId)}/${analyticalDatasetProfileId}`;
};
export const getAnalyticalDatasetVersionPath = (
  activeProjectId: string,
  analyticalDatasetProfileId: string,
  analyticalDatasetVersionId: string
) => {
  return `${getAnalyticalDatasetDetailPath(
    activeProjectId,
    analyticalDatasetProfileId
  )}/${ANALYTICAL_DATASET_VERSION_PATH}/${analyticalDatasetVersionId}`;
};
// Analysis artifact paths (profile id + version id; the unversioned
// form hits the version resolver). Defined here rather
// than in feature-analysis so lower libs can build them without importing
// that feature lib — which would close a dependency cycle. Unlike analytical
// datasets, there is no standalone list path: the unified project-artifacts
// list is the only entry surface.
export const ANALYSIS_PATH = 'analysis';
export const ANALYSIS_VERSION_PATH = 'analysis-version';
export const getAnalysisDetailPath = (activeProjectId: string, analysisProfileId: string) => {
  return `${getProjectPath(activeProjectId)}/${ANALYSIS_PATH}/${analysisProfileId}`;
};
export const getAnalysisVersionPath = (
  activeProjectId: string,
  analysisProfileId: string,
  analysisVersionId: string
) => {
  return `${getAnalysisDetailPath(
    activeProjectId,
    analysisProfileId
  )}/${ANALYSIS_VERSION_PATH}/${analysisVersionId}`;
};

export const getProjectConceptGroupPath = (activeProjectId: string, conceptGroupId: string) => {
  return `${getProjectPath(activeProjectId)}/concept-group/${conceptGroupId}`;
};

// OR-6126: read-only review surface for a *proposed* concept group (a preview
// keyed by previewId, not yet created). Rendered in the project page's right
// pane; the agent's concept_group chat chip routes here. The backend computes
// the matching route string when it emits the chip.
export const getProjectConceptGroupPreviewPath = (activeProjectId: string, previewId: string) => {
  return `${getProjectPath(activeProjectId)}/concept-group-preview/${previewId}`;
};

// OR-6504: read-only review surface for a proposed variable GROUP — several
// variables sharing one definition, keyed by family id. Nothing is created until
// the researcher approves, so there is no asset id to route by and the page
// renders from the replayed approval checkpoint. Draft-stage only: approval
// replaces the group with N individual variables, each with its own route.
export const getProjectVariableGroupPath = (activeProjectId: string, familyId: string) => {
  return `${getProjectPath(activeProjectId)}/variable-group/${familyId}`;
};

// Cohort Getters
export const getCohortsPath = (activeProjectId: string) => {
  return `${getProjectsBasePath()}/${activeProjectId}/${COHORTS_PATH}`;
};
export const getCohortExportForAnalysisPath = (activeProjectId: string, activeCohortId: string) => {
  return `${getCohortsPath(activeProjectId)}/${activeCohortId}/export`;
};

// Cohort Builder Path Getters
export const getCohortBuilderPath = (
  activeProjectId: string,
  activeCohortId: string,
  latestVersionId: string
) => {
  return `${getCohortsPath(
    activeProjectId
  )}/${activeCohortId}/cohort-version/${latestVersionId}/${COHORT_BUILDER_PATH}`;
};

// Analytical Variable Edit Path Getter
export const getAnalyticalVariableEditPath = (
  activeProjectId: string,
  activeCohortId: string,
  latestVersionId: string,
  analyticalVariableId: string
) => {
  return `${getCohortsPath(
    activeProjectId
  )}/${activeCohortId}/cohort-version/${latestVersionId}/${ANALYTICAL_VARIABLE_EDIT_PATH}/${analyticalVariableId}/edit`;
};

// Data Reliability Getters
// TODO: cleanup with data_reliability ff — Data Reliability is a top-level, project-independent
// app; its paths are no longer project-scoped.
export const getDataReliabilityAccuracyPath = () => {
  return `${getDataReliabilityBasePath()}?${PAGE_TABS_URL_PARAM}=${ACCURACY_PATH}`;
};
export const getDataReliabilityCompletenessPath = () => {
  return `${getDataReliabilityBasePath()}?${PAGE_TABS_URL_PARAM}=${COMPLETENESS_PATH}`;
};
export const getDataReliabilityTraceabilityPath = () => {
  return `${getDataReliabilityBasePath()}?${PAGE_TABS_URL_PARAM}=${TRACEABILITY_PATH}`;
};
